import type { CustomEntry, Journey, Place, Stay } from '@my-fullstack-app/itinerary-types';
import { addMinutes } from '@kiips/ui';
import { generateId } from './id';
import { nextDayIso } from './dates';
import { DEFAULT_CHECK_OUT_TIME } from './add-item-draft';

/**
 * Converting an existing placeholder (CustomEntry) into a richer itinerary
 * entity. A placeholder is a rough "figure this out later" time block;
 * once the user knows what it actually is, they convert it.
 *
 * `'visit'` is the UI term for a `Place` — it matches the add-menu speed
 * dial ("Stay" / "Journey" / "Visit"). Stay and Journey are trip-scoped;
 * a Visit lands on the placeholder's own day.
 */
export type ConversionTarget = 'stay' | 'journey' | 'visit';

/**
 * Cap an 'HH:mm' value at the last minute of the day. A placeholder
 * block can legitimately carry a 24:00 boundary — its endTime defaults
 * to start + 1h, clamped at end-of-day — but a Stay / Journey stamps
 * the time into an ISO datetime, where 'T24:00' is invalid.
 */
function clampToDayEnd(hhmm: string): string {
  return hhmm >= '24:00' ? '23:59' : hhmm;
}

/**
 * Compose the note for a converted entity. A placeholder's `title` and
 * `subtitle` don't always have a structured home on the target type — a
 * Journey carries no title, and nothing carries a subtitle — so any
 * field that can't be mapped is folded in as a leading note line. That
 * keeps the conversion lossless. `carried` lines come first, then the
 * placeholder's original note body.
 */
export function composeConvertedNote(
  carried: string[],
  existingNote: string | undefined,
): string | undefined {
  const lines = carried.map((s) => s.trim()).filter(Boolean);
  const body = (existingNote ?? '').trim();
  const parts: string[] = [];
  if (lines.length) parts.push(lines.join('\n'));
  if (body) parts.push(body);
  return parts.length > 0 ? parts.join('\n\n') : undefined;
}

export type ConversionResult =
  | { target: 'stay'; entity: Stay; note: string | undefined }
  | { target: 'journey'; entity: Journey; note: string | undefined }
  | { target: 'visit'; entity: Place; note: string | undefined };

/**
 * Build the entity an existing placeholder should become. The caller
 * (custom-slice `convertCustom`) inserts `entity`, writes `note` to
 * `notesMap[entity.id]`, carries the tag over, and removes the source
 * placeholder — all in one atomic, undoable store update.
 *
 * `dayDate` is the 'YYYY-MM-DD' of the placeholder's day; it stamps the
 * calendar date onto the ISO datetimes a trip-scoped Stay / Journey need
 * (a placeholder only knows wall-clock 'HH:mm'). `existingNote` is the
 * placeholder's current `notesMap` entry, if any.
 */
export function buildConversion(
  custom: CustomEntry,
  target: ConversionTarget,
  dayDate: string,
  existingNote: string | undefined,
): ConversionResult {
  const id = generateId();
  const start = custom.startTime;
  const end = custom.endTime;
  const hasCosts = (custom.costs?.length ?? 0) > 0;

  if (target === 'stay') {
    // A Stay is multi-day by definition, so the placeholder's same-day
    // endTime can't map to a checkout. Seed checkout to the next day at
    // the same hour a directly-added stay uses, from the one constant
    // both paths share.
    const entity: Stay = {
      id,
      place: { id: `legacy:${id}`, name: custom.title },
      customLabel: custom.title,
      checkIn: `${dayDate}T${clampToDayEnd(start)}`,
      checkOut: `${nextDayIso(dayDate)}T${DEFAULT_CHECK_OUT_TIME}`,
      ...(hasCosts ? { booking: { costs: custom.costs } } : {}),
    };
    return {
      target,
      entity,
      note: composeConvertedNote(custom.subtitle ? [custom.subtitle] : [], existingNote),
    };
  }

  if (target === 'journey') {
    // A placeholder's start/end map naturally onto departure/arrival.
    // Fall back to a 1-hour leg when there is no usable endTime, and
    // clamp both anchors so they never overflow into an invalid datetime.
    const depart = clampToDayEnd(start);
    const arrive = clampToDayEnd(end && end > start ? end : addMinutes(start, 60));
    const entity: Journey = {
      id,
      mode: 'flight',
      origin: { place: { id: `legacy:${id}:origin`, name: '' }, time: `${dayDate}T${depart}` },
      destination: { place: { id: `legacy:${id}:destination`, name: '' }, time: `${dayDate}T${arrive}` },
      ...(hasCosts ? { booking: { costs: custom.costs } } : {}),
    };
    // A Journey has no title field, so the placeholder's title is folded
    // into the note alongside the subtitle.
    return {
      target,
      entity,
      note: composeConvertedNote(
        [custom.title, ...(custom.subtitle ? [custom.subtitle] : [])],
        existingNote,
      ),
    };
  }

  // visit → Place. A coordinate-less manual place is a supported state
  // (locations the catalog doesn't know); the user can pick a real place
  // later on the place detail screen.
  const entity: Place = {
    id,
    placeId: '',
    name: custom.title,
    startTime: start,
    endTime: end ?? addMinutes(start, 60),
    ...(hasCosts ? { costs: custom.costs } : {}),
  };
  return {
    target,
    entity,
    note: composeConvertedNote(custom.subtitle ? [custom.subtitle] : [], existingNote),
  };
}
